import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { Modal } from '../../components/ui/Modal';
import { VerifyingOverlay } from '../../components/ui/VerifyingOverlay';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { CustomerFeedbackForm } from '../../components/forms/CustomerFeedbackForm';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  cancelCustomerFeedback,
  cffSignedCopyToPdfBase64,
  downloadCffSignatureCopyBlob,
  fetchCustomerFeedbackSubmission,
  isCffCancelled,
  isCffLocked,
  isCffPosted,
  isCffSubmitted,
  postCustomerFeedback,
  updateCustomerFeedback,
  uploadCffSignatureCopy,
} from '../../lib/customerFeedbackSubmissions';
import {
  downloadCffPdfFromSharePoint,
  fetchCffMasterWorkbookBase64,
  rebuildCffMonthOnSharePoint,
  uploadCffPdfToSharePoint,
} from '../../lib/applicationFormSettings';
import { buildCffPdfSourceBase64, downloadCffExcel } from '../../lib/customerFeedbackExcel';
import { formatSubmittedAt, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import {
  EMPTY_CUSTOMER_FEEDBACK_FORM,
  type ApplicationSubmission,
  type CustomerFeedbackFormData,
} from '../../types/applicationForm';
import type { CffNewNavigateState } from './CustomerFeedbackNewPage';

async function queueCffSharePointSync(
  submissionId: string,
  form: CustomerFeedbackFormData,
  applicationNo: string,
): Promise<
  | { ok: true; fileName?: string; folderPath?: string; warning?: string }
  | { ok: false; error: string }
> {
  try {
    const result = await rebuildCffMonthOnSharePoint(applicationNo, { submissionId, form });
    const displayPath =
      (typeof result.display_path === 'string' && result.display_path.trim()) ||
      (typeof result.folder_path === 'string' && result.folder_path.trim()) ||
      undefined;
    return {
      ok: true,
      fileName: typeof result.excel_file_name === 'string' ? result.excel_file_name : undefined,
      folderPath: displayPath,
      warning: typeof result.warning === 'string' ? result.warning : undefined,
    };
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    console.warn('CFF SharePoint sync queue failed:', err);
    return { ok: false, error };
  }
}

function formatExcelSyncedNotice(prefix: string, fileName?: string, folderPath?: string): string {
  const file = fileName?.trim() || 'CFF-….xlsx';
  if (folderPath?.trim()) {
    return `${prefix} Excel synced as ${file} to ${folderPath.trim()}.`;
  }
  return `${prefix} Excel synced as ${file}.`;
}

export function CustomerFeedbackDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath, kind } = usePortal();
  const isDem = kind === 'dem';
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const listPath = basePath ? `${basePath}/customer-feedback` : '/customer-feedback';
  const pageClass = `af-applications-page af-form-page${isDem ? ' cff-page--floating' : ''}`;

  const [row, setRow] = useState<ApplicationSubmission | null>(null);
  const [form, setForm] = useState<CustomerFeedbackFormData>(EMPTY_CUSTOMER_FEEDBACK_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pdfOverlay, setPdfOverlay] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturesComplete, setSignaturesComplete] = useState(false);
  const remarksSavingRef = useRef(false);
  const pdfTimerRef = useRef<number | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const lastNoticeToastRef = useRef<string | null>(null);
  const shownStatusTipsRef = useRef<Set<string>>(new Set());
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  const stopPdfTimer = useCallback(() => {
    if (pdfTimerRef.current != null) {
      window.clearInterval(pdfTimerRef.current);
      pdfTimerRef.current = null;
    }
  }, []);

  const clearPdfOverlay = useCallback(() => {
    stopPdfTimer();
    setPdfOverlay(null);
  }, [stopPdfTimer]);

  useEffect(() => () => clearPdfOverlay(), [clearPdfOverlay]);

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  useEffect(() => {
    if (notice && notice !== lastNoticeToastRef.current) {
      lastNoticeToastRef.current = notice;
      pushToast(pageFeedbackToToast({ kind: 'success', message: notice }));
    }
    if (!notice) lastNoticeToastRef.current = null;
  }, [notice, pushToast]);

  useEffect(() => {
    const flash = (location.state ?? null) as CffNewNavigateState | null;
    if (!flash?.cffNotice && !flash?.cffError) return;
    if (flash.cffNotice) setNotice(flash.cffNotice);
    if (flash.cffError) setError(flash.cffError);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const currentUserLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  const isPosted = isCffPosted(row?.status);
  const isCancelled = isCffCancelled(row?.status);
  const isSubmitted = isCffSubmitted(row?.status);
  const locked = isCffLocked(row?.status);
  /** Main fields locked when posted/cancelled, or submitted (view) unless Edit. */
  const fieldsLocked = locked || (isSubmitted && !editMode);
  /** Internal remarks stay editable after Submit / Post. */
  const allowRemarksEdit = !isCancelled && (isSubmitted || isPosted);
  /** Picker / Packer / Driver only while Edit is active (Submitted only — never after Post). */
  const allowWarehouseRoles = isSubmitted && editMode;

  useEffect(() => {
    if (!row) return;
    let tip: { kind: 'info' | 'success'; title: string; message: string } | null = null;
    if (isSubmitted && editMode) {
      tip = {
        kind: 'info',
        title: 'Editing submitted form',
        message: 'Save when done, or Post when ready.',
      };
    } else if (isSubmitted && !editMode) {
      tip = {
        kind: 'info',
        title: 'Submitted',
        message:
          'Internal remarks stay editable. Use Edit for Picker / Packer / Driver or other fields, then Post with a signed copy when ready.',
      };
    } else if (isPosted) {
      const postedBy = (form.posted_by || '').trim();
      const copyName = (form.signature_copy_name || '').trim();
      tip = {
        kind: 'info',
        title: 'Posted',
        message: `${postedBy ? `By ${postedBy}. ` : ''}${
          copyName ? `Signed copy: ${copyName}. ` : ''
        }Form is locked; Internal remarks stay editable.`,
      };
    } else if (isCancelled) {
      const cancelledBy = (form.cancelled_by || '').trim();
      tip = {
        kind: 'info',
        title: 'Cancelled',
        message: `${cancelledBy ? `By ${cancelledBy}. ` : ''}The form is locked.`,
      };
    }
    if (!tip) return;
    const key = `${row.id}:${tip.title}`;
    if (shownStatusTipsRef.current.has(key)) return;
    shownStatusTipsRef.current.add(key);
    pushToast(pageFeedbackToToast(tip));
  }, [
    row,
    form.posted_by,
    form.signature_copy_name,
    form.cancelled_by,
    isSubmitted,
    isPosted,
    isCancelled,
    editMode,
    pushToast,
  ]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCustomerFeedbackSubmission(id);
      setRow(data);
      setEditMode(false);
      const fd = data.form_data as CustomerFeedbackFormData;
      setForm({
        ...EMPTY_CUSTOMER_FEEDBACK_FORM,
        ...fd,
        internal_remarks: fd.internal_remarks ?? '',
        invoice_issued_by: fd.invoice_issued_by ?? '',
        items:
          Array.isArray(fd.items) && fd.items.length
            ? fd.items
            : EMPTY_CUSTOMER_FEEDBACK_FORM.items,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    enabled: Boolean(id),
    tables: ['customer_feedback_submissions'],
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load();
    },
  });

  const buildPayload = useCallback(
    (): CustomerFeedbackFormData => ({
      ...form,
      submitted_by: form.submitted_by.trim() || currentUserLabel,
    }),
    [form, currentUserLabel],
  );

  const handleSave = async () => {
    if (!row || !isSubmitted || !editMode) return;
    if (!form.company_name.trim() && !form.company_code.trim()) {
      setError('Enter company name or company code.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await updateCustomerFeedback(row.id, buildPayload());
      setRow(updated);
      setForm(updated.form_data as CustomerFeedbackFormData);
      setEditMode(false);
      setNotice('Changes saved. Syncing Excel to SharePoint month folder…');
      setBusy(false);
      const sync = await queueCffSharePointSync(
        updated.id,
        updated.form_data as CustomerFeedbackFormData,
        updated.application_no,
      );
      if (sync.ok) {
        if (sync.warning) {
          setNotice(sync.warning);
          setError(null);
        } else {
          setNotice(formatExcelSyncedNotice('Saved.', sync.fileName, sync.folderPath));
        }
      } else {
        setNotice(
          'Saved in portal. SharePoint sync failed — close the Excel file if it is open in Teams, then save again.',
        );
        setError(sync.error);
      }
    } catch (err) {
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to save.');
      setBusy(false);
    }
  };

  const handlePartialFieldsBlur = async () => {
    if (!row || !allowRemarksEdit || editMode || busy || remarksSavingRef.current) return;
    remarksSavingRef.current = true;
    try {
      const updated = await updateCustomerFeedback(row.id, buildPayload());
      setRow(updated);
      setForm(updated.form_data as CustomerFeedbackFormData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save remarks.');
    } finally {
      remarksSavingRef.current = false;
    }
  };

  const handleExcel = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = buildPayload();
      const existing = await fetchCffMasterWorkbookBase64(row.application_no).catch(() => null);
      await downloadCffExcel(payload, row.application_no, existing);
      setNotice('Excel downloaded.');
    } catch (err) {
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to download Excel.');
    } finally {
      setBusy(false);
    }
  };

  const triggerBrowserDownload = (blob: Blob, filename: string) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  };

  const handlePdf = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    clearPdfOverlay();
    try {
      const path = (form.signature_copy_path || '').trim();
      if (isPosted && path) {
        const blob = await downloadCffSignatureCopyBlob(path);
        const safeNo = row.application_no.replace(/[\\/:*?"<>|]+/g, '_');
        triggerBrowserDownload(blob, `${safeNo} SIGNED COPY.pdf`);
        setNotice('Signed copy downloaded.');
        return;
      }

      if (!isSubmitted && !isPosted) {
        setError('PDF download is available after Submit.');
        return;
      }

      const startedAt = Date.now();
      setPdfOverlay('Converting PDF…');
      pdfTimerRef.current = window.setInterval(() => {
        const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
        setPdfOverlay(`Converting PDF… (${elapsedSec}s)`);
      }, 1000);

      const workingForm = buildPayload();
      const creatorName =
        workingForm.submitted_by.trim() || currentUserLabel || row.submitted_by?.trim() || '';
      const issuer = workingForm.invoice_issued_by.trim();
      const sheetXlsxBase64 = await buildCffPdfSourceBase64(workingForm, row.application_no, {
        omitSubmitterNameForStamp: Boolean(creatorName),
      });
      await downloadCffPdfFromSharePoint(row.id, sheetXlsxBase64, {
        download: true,
        creatorName: creatorName || undefined,
        creatorCaption: issuer ? `(Inv. issued by: ${issuer})` : undefined,
        stamp: isCancelled ? 'cancelled' : 'none',
      });
      clearPdfOverlay();
      setNotice('PDF downloaded.');
    } catch (err) {
      clearPdfOverlay();
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to download PDF.');
    } finally {
      clearPdfOverlay();
      setBusy(false);
    }
  };

  const closePostModal = () => {
    if (busy) return;
    setPostOpen(false);
    setSignatureFile(null);
    setSignaturesComplete(false);
  };

  const handlePostConfirm = async () => {
    if (!row || locked || !signatureFile) {
      setError('Please upload a signed copy before posting.');
      return;
    }
    if (!signaturesComplete) {
      setError('Confirm that all required signatures are on the file.');
      return;
    }
    if (!form.company_name.trim() && !form.company_code.trim()) {
      setError('Enter company name or company code.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await updateCustomerFeedback(row.id, buildPayload());
      const uploaded = await uploadCffSignatureCopy(saved.id, signatureFile);
      const updated = await postCustomerFeedback(
        saved.id,
        uploaded.fileName,
        uploaded.path,
        currentUserLabel || undefined,
      );
      setRow(updated);
      setForm(updated.form_data as CustomerFeedbackFormData);
      setPostOpen(false);
      setSignatureFile(null);
      setSignaturesComplete(false);

      setNotice('Posted. Uploading signed copy to Teams…');
      const blob = await downloadCffSignatureCopyBlob(uploaded.path);
      const pdfB64 = await cffSignedCopyToPdfBase64(blob, uploaded.fileName);
      const safeNo = updated.application_no.replace(/[\\/:*?"<>|]+/g, '_');
      const signedCopyName = `${safeNo} SIGNED COPY.pdf`;
      const sync = await queueCffSharePointSync(
        updated.id,
        updated.form_data as CustomerFeedbackFormData,
        updated.application_no,
      );
      try {
        const pdfUpload = await uploadCffPdfToSharePoint(updated.id, pdfB64, {
          fileName: signedCopyName,
          asSignedCopy: true,
        });
        const folder =
          (typeof pdfUpload.display_path === 'string' && pdfUpload.display_path.trim()) ||
          (typeof pdfUpload.folder_path === 'string' && pdfUpload.folder_path.trim()) ||
          '';
        const excelNote = sync.ok
          ? formatExcelSyncedNotice('', sync.fileName, sync.folderPath)
          : ' Excel SharePoint sync failed.';
        setNotice(
          `Posted. Signed copy uploaded as ${signedCopyName}${folder ? ` to ${folder}` : ''}.${excelNote ? ` ${excelNote}` : ''} Main fields are locked; remarks and warehouse roles stay editable.`,
        );
        if (!sync.ok) setError(sync.error);
      } catch (uploadErr) {
        setNotice(
          'Posted in portal. Signed copy saved, but Teams PDF upload failed. Remarks and warehouse roles stay editable.',
        );
        setError(uploadErr instanceof Error ? uploadErr.message : 'Teams signed PDF upload failed.');
      }
    } catch (err) {
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to post.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!row || isCancelled) return;
    if (
      !window.confirm(
        'Cancel this customer feedback form? It will become read-only and cannot be posted.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (!locked) {
        await updateCustomerFeedback(row.id, buildPayload()).catch(() => undefined);
      }
      const updated = await cancelCustomerFeedback(row.id, currentUserLabel || undefined);
      setRow(updated);
      setForm(updated.form_data as CustomerFeedbackFormData);
      setNotice('Cancelled. The form is locked.');
    } catch (err) {
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to cancel.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={pageClass}>
        <p className="page-lead">Loading…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className={pageClass}>
        {isDem ? (
          <>
            <Link to={listPath} className="btn-secondary delivery-page__back">
              Back to list
            </Link>
            <p className="alert alert-error">{error ?? 'Not found.'}</p>
          </>
        ) : (
          <PageHeader
            title="Customer Feedback Form"
            description={error ?? 'Not found.'}
            actions={
              <Link to={listPath} className="btn-secondary btn-sm">
                Back to list
              </Link>
            }
          />
        )}
      </div>
    );
  }

  const displaySubmittedBy = form.submitted_by.trim() || currentUserLabel;
  return (
    <div className={pageClass}>
      {isDem ? (
        <div className="dem-floating-page-actions dem-blanket-detail__toolbar">
          <div className="dem-blanket-detail__toolbar-start">
            <Link to={listPath} className="btn-secondary">
              Back to list
            </Link>
            <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
          </div>
        </div>
      ) : (
        <PageHeader
          title={row.application_no}
          description={`${row.company_name} · ${formatSubmittedAt(row.submitted_at)}`}
          actions={
            <div className="af-applications-page__actions">
              <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
              <Link to={listPath} className="btn-secondary btn-sm">
                Back to list
              </Link>
            </div>
          }
        />
      )}

      {pdfOverlay ? <VerifyingOverlay message={pdfOverlay} /> : null}
      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="card cff-form-card">
        <CustomerFeedbackForm
          value={form}
          onChange={setForm}
          applicationNo={row.application_no}
          submissionId={row.id}
          submittedByDisplayName={displaySubmittedBy}
          disabled={fieldsLocked}
          allowInternalRemarks={allowRemarksEdit}
          allowWarehouseRoles={allowWarehouseRoles}
          onInternalRemarksBlur={() => void handlePartialFieldsBlur()}
        />
        <div className="cff-form__actions cff-form__actions--split">
          <div className="cff-form__actions-group">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => void handleExcel()}
            >
              Download Excel
            </button>
            {isPosted ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy || !(form.signature_copy_path || '').trim()}
                onClick={() => void handlePdf()}
              >
                Download signed copy
              </button>
            ) : (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy || !isSubmitted}
                onClick={() => void handlePdf()}
              >
                Download PDF
              </button>
            )}
          </div>
          <div className="cff-form__actions-group">
            {!locked ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleCancel()}
              >
                Cancel
              </button>
            ) : null}
            {isSubmitted && !editMode ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => {
                  setEditMode(true);
                  setNotice(null);
                  setError(null);
                }}
              >
                Edit
              </button>
            ) : null}
            {isSubmitted && editMode ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleSave()}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            ) : null}
            {isSubmitted ? (
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={() => {
                  setSignatureFile(null);
                  setSignaturesComplete(false);
                  setPostOpen(true);
                  setError(null);
                  setNotice(null);
                }}
              >
                Post
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <Modal
        title="Post CFF"
        open={postOpen}
        onClose={closePostModal}
        panelClassName="cff-post-modal"
      >
        <div className="cff-post-modal__body">
          <p className="cff-post-modal__lead">
            Upload the signed copy. Form locks after post.
          </p>

          <label className="cff-post-modal__upload">
            <span className="cff-post-modal__upload-label">
              Signed copy <span className="af-form__req">*</span>
            </span>
            <span className="cff-post-modal__upload-box">
              <span className="cff-post-modal__upload-title">
                {signatureFile ? signatureFile.name : 'Choose PDF or image'}
              </span>
              <span className="cff-post-modal__upload-hint">
                {signatureFile
                  ? 'Click to replace'
                  : `Saves as ${row.application_no} SIGNED COPY.pdf`}
              </span>
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
                disabled={busy}
                onChange={(e) => setSignatureFile(e.target.files?.[0] ?? null)}
              />
            </span>
          </label>

          <label className="cff-post-modal__check">
            <input
              type="checkbox"
              checked={signaturesComplete}
              disabled={busy}
              onChange={(e) => setSignaturesComplete(e.target.checked)}
            />
            <span>
              All required signatures are complete on this file
              <span className="af-form__req">*</span>
            </span>
          </label>
        </div>

        <div className="cff-post-modal__actions">
          <button type="button" className="btn-secondary" disabled={busy} onClick={closePostModal}>
            Back
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !signatureFile || !signaturesComplete}
            onClick={() => void handlePostConfirm()}
          >
            {busy ? 'Posting…' : 'Confirm Post'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
